import { useMemo, useRef, useState } from 'react';
import type { AiJobTrace, TraceEvent } from '../../lib/supabase.types';
import { Badge, Button, SegmentedControl } from '../ui';

type FilterId = 'all' | 'tool' | 'file' | 'thinking' | 'message';

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'tool', label: 'Tools' },
  { id: 'file', label: 'Files' },
  { id: 'thinking', label: 'Thinking' },
  { id: 'message', label: 'Messages' },
];

const KIND_LABEL: Record<AiJobTrace['kind'], string> = {
  spec: 'Spec',
  report: 'Build',
  unwind: 'Unwind',
  approval: 'Approval',
};

function formatEventTime(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

function countFor(events: TraceEvent[], id: FilterId): number {
  if (id === 'all') return events.length;
  return events.filter((event) => event.categories?.includes(id)).length;
}

function matchesFilter(event: TraceEvent, filter: FilterId): boolean {
  if (filter === 'all') return true;
  return event.categories?.includes(filter);
}

function categoryTone(event: TraceEvent): 'accent' | 'info' | 'warn' | 'neutral' {
  if (event.isError) return 'warn';
  if (event.categories?.includes('file')) return 'info';
  if (event.categories?.includes('thinking')) return 'neutral';
  if (event.categories?.includes('tool')) return 'accent';
  return 'neutral';
}

function categoryLabel(event: TraceEvent): string {
  if (event.categories?.includes('file')) return 'File';
  if (event.categories?.includes('thinking')) return 'Thinking';
  if (event.categories?.includes('tool')) return 'Tool';
  return 'Message';
}

function JsonBlock({ value }: { value: unknown }) {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  if (!text) return null;
  return (
    <pre
      style={{
        margin: 0,
        maxHeight: 240,
        overflow: 'auto',
        padding: '10px 12px',
        background: 'var(--surface-inset)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-xs)',
        color: 'var(--text-body)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </pre>
  );
}

function EventDetail({
  event,
  onViewInTrace,
}: {
  event: TraceEvent;
  onViewInTrace: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Badge tone={categoryTone(event)}>{categoryLabel(event)}</Badge>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', fontWeight: 500 }}>
          {event.title}
        </span>
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {formatEventTime(event.ts)}
        </span>
      </div>
      {event.tool ? (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          Tool: {event.tool}
          {event.path ? ` · ${event.path}` : ''}
        </div>
      ) : null}
      {event.args != null ? (
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            Arguments
          </div>
          <JsonBlock value={event.args} />
        </div>
      ) : null}
      {event.result ? (
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            {event.isError ? 'Error' : 'Result'}
          </div>
          <JsonBlock value={event.result} />
        </div>
      ) : null}
      {event.thinking ? (
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            Thinking
          </div>
          <JsonBlock value={event.thinking} />
        </div>
      ) : null}
      {event.text ? (
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            Message
          </div>
          <JsonBlock value={event.text} />
        </div>
      ) : null}
      <div>
        <Button size="sm" variant="ghost" onClick={onViewInTrace}>
          View in full trace
        </Button>
      </div>
    </div>
  );
}

function TraceCard({ trace }: { trace: AiJobTrace }) {
  const events = Array.isArray(trace.events) ? trace.events : [];
  const [filter, setFilter] = useState<FilterId>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () => events.filter((event) => matchesFilter(event, filter)),
    [events, filter]
  );
  const selected = events.find((event) => event.id === selectedId) ?? null;

  const jumpToFullTrace = (id: string) => {
    setFilter('all');
    setSelectedId(id);
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector(`[data-trace-id="${id}"]`);
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ block: 'center' });
      }
    });
  };

  const options = FILTERS.map((item) => ({
    id: item.id,
    label: `${item.label} ${countFor(events, item.id)}`,
  }));

  return (
    <div
      style={{
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid var(--border-hairline)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
        <div className="t-eyebrow">
          Agent trace · {KIND_LABEL[trace.kind]} v{trace.job_version}
        </div>
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {trace.event_count} events
        </span>
      </div>

      <SegmentedControl
        options={options}
        value={filter}
        onChange={(id) => {
          setFilter(id as FilterId);
          setSelectedId(null);
        }}
        style={{ width: '100%', overflowX: 'auto' }}
      />

      <div
        ref={listRef}
        style={{
          maxHeight: 280,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        {filtered.length === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
            Nothing in this category.
          </p>
        ) : (
          filtered.map((event) => {
            const active = event.id === selectedId;
            return (
              <div
                key={event.id}
                data-trace-id={event.id}
                className="hover-wash"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  width: '100%',
                  padding: '8px 10px',
                  background: active ? 'var(--accent-soft)' : 'transparent',
                  border: active ? '1px solid rgba(196, 92, 62, 0.28)' : '1px solid transparent',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                <button
                  type="button"
                  className="pressable"
                  onClick={() => setSelectedId(event.id === selectedId ? null : event.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    minWidth: 0,
                    flex: 1,
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', paddingTop: 2, flexShrink: 0 }}>
                    {formatEventTime(event.ts)}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--text-body)',
                        fontWeight: 500,
                      }}
                    >
                      {event.title}
                    </span>
                    {event.summary && event.summary !== event.title ? (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--text-faint)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {event.summary}
                      </span>
                    ) : null}
                  </span>
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                  <Badge tone={categoryTone(event)}>{categoryLabel(event)}</Badge>
                  <button
                    type="button"
                    className="pressable"
                    onClick={() => jumpToFullTrace(event.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    View in full trace
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {selected ? (
        <div
          style={{
            padding: '12px',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <EventDetail event={selected} onViewInTrace={() => jumpToFullTrace(selected.id)} />
        </div>
      ) : null}
    </div>
  );
}

export default function AiJobTrace({ traces }: { traces: AiJobTrace[] }) {
  if (!traces.length) return null;
  return (
    <>
      {traces.map((trace) => (
        <TraceCard key={trace.id} trace={trace} />
      ))}
    </>
  );
}
